/**
 * フロントエンド（src/）と Pages Functions（functions/）で共有する型。
 * /api の入出力契約はここだけで定義する。プロバイダを差し替えてもこの形は変えない。
 */

/** [経度, 緯度]（GeoJSON と同じ順序） */
export type LngLat = [number, number];

export type ManeuverType =
  | 'depart'
  | 'arrive'
  | 'straight'
  | 'slight-left'
  | 'left'
  | 'sharp-left'
  | 'slight-right'
  | 'right'
  | 'sharp-right'
  | 'uturn'
  | 'roundabout'
  | 'merge'
  | 'ramp'
  | 'unknown';

export interface RouteStep {
  /** 案内文（日本語） */
  instruction: string;
  maneuver: ManeuverType;
  /** このステップの距離 [m] */
  distance: number;
  /** このステップの所要時間 [s] */
  duration: number;
  /** geometry 配列内での、このステップの開始インデックス */
  startIndex: number;
  /** 道路名（取れる場合） */
  name?: string;
}

export interface Route {
  /** ルート形状（[経度, 緯度] の配列） */
  geometry: LngLat[];
  /** 総距離 [m] */
  distance: number;
  /** 総所要時間 [s] */
  duration: number;
  steps: RouteStep[];
  /**
   * 高速道路（有料道路）の区間。geometry のインデックスで [開始, 終了]。
   * 高速道路モードの切り替えと、IC・JCT・SA・PA の照合に使う。取れないプロバイダでは省略
   */
  highways?: [number, number][];
}

/**
 * ルートの種類。GET /api/route?from=経度,緯度&to=経度,緯度&type=fastest
 *   fastest   = 高速優先（いちばん速い）
 *   nohighway = 一般道優先（有料道路・高速道路を避ける）
 *   shortest  = 最短距離
 * 省略時は fastest。プロバイダが種類に対応していなければ同じルートを返してよい
 */
export type RouteType = 'fastest' | 'nohighway' | 'shortest';
export const ROUTE_TYPES: RouteType[] = ['fastest', 'nohighway', 'shortest'];

export interface RouteResponse {
  route: Route;
  /** どのプロバイダが返したか（デバッグ・帰属表示用） */
  provider: string;
  /** 帰属表示（UI に出す） */
  attribution?: string;
}

/**
 * GET /api/geocode?q=検索語&near=経度,緯度
 * near は任意。近い結果を優先するための基準点で、ブラウザ側で 0.1 度（約10km）に丸めて送る。
 */
export interface GeocodeResult {
  label: string;
  position: LngLat;
}

export interface GeocodeResponse {
  results: GeocodeResult[];
  provider: string;
  attribution?: string;
}

export interface ApiError {
  error: string;
  /** 'rate_limited' | 'upstream_error' | 'bad_request' | 'quota_exceeded' | 'no_route'（道路が見つからない）など */
  code: string;
}
